import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ConfigurationType, ConnectorTypes } from '@bciti/types';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Connector } from 'src/app/interfaces/config/config.interface';
import { ConfigurationService } from 'src/app/services/configuration/configuration.service';

@Component({
    selector: 'app-add-edit-connector-dialog-form',
    templateUrl: './add-edit-connector-dialog-form.component.html',
    styleUrls: ['./add-edit-connector-dialog-form.component.scss'],
})
export class AddEditConnectorDialogFormComponent implements OnInit, OnDestroy {
    destroy$ = new Subject();

    formPreparing = true;
    loading = false;

    addConnectorForm = new FormGroup({
        configurations: new FormGroup({}),
    });

    get disabled() {
        return this.addConnectorForm.invalid || this.loading;
    }

    constructor(
        private toastr: ToastrService,
        private configurationService: ConfigurationService,
        private dialogRef: MatDialogRef<AddEditConnectorDialogFormComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            connector: Connector;
            orgTag: string;
            type: ConnectorTypes;
            existingConnector?: any;
            configuredConnectors?: any[];
        },
    ) {}

    ngOnInit(): void {
        this.addConnectorForm.addControl('tag', new FormControl(this.data.connector.tag, [Validators.required]));
        // The tab's type, not the catalogue entry's: a multi-purpose connector carries
        // a single catalogue type ('Requests' for PSD Citywide) while being configurable
        // under several tabs, so the catalogue value would be wrong here (BD-11295).
        this.addConnectorForm.addControl('type', new FormControl(this.data.type, [Validators.required]));
        this.data.connector.configurations.forEach((config) => {
            (this.addConnectorForm.get('configurations') as FormGroup).addControl(
                config.key,
                new FormControl(config.inputType === 'boolean' ? false : undefined, config.required ? [Validators.required] : []),
            );
        });
        if (this.data.existingConnector) {
            this.addConnectorForm.get('configurations').patchValue(this.data.existingConnector.configurations);
        }
        this.formPreparing = false;
    }

    closeDialog() {
        this.dialogRef.close();
    }

    convertFormTypes() {
        for (const config of this.data.connector.configurations) {
            const formControl = this.addConnectorForm.get('configurations').get(config.key);

            switch (config.inputType) {
                case 'number':
                    formControl.setValue(+formControl.value);
                    break;
                case 'boolean':
                    formControl.setValue(!!formControl.value);
                    break;
                case 'string':
                default:
                    formControl.setValue(formControl.value);
                    break;
            }
        }
    }

    submitForm() {
        if (this.addConnectorForm.valid) {
            this.loading = true;
            const data = {
                // Keyed on (tag, type): dropping every entry sharing the tag would wipe the
                // same connector's configuration under its other types.
                connectors: this.data.configuredConnectors.filter(
                    (cc) => !(cc.tag === this.data.connector.tag && cc.type === this.data.type),
                ),
            };
            this.convertFormTypes();
            data.connectors.push(this.addConnectorForm.value);

            const removeNull = (obj) => {
                Object.keys(obj).forEach((key) => {
                    if (obj[key] && typeof obj[key] === 'object') {
                        removeNull(obj[key]);
                    } else if (obj[key] === null) {
                        delete obj[key];
                    }
                });
            };
            removeNull(data.connectors);

            const payload = {
                configuration: {
                    key: ConfigurationType.CONFIGUREDCONNECTORS,
                    data,
                },
            };
            this.configurationService
                .patchConfigurations(this.data.orgTag, payload)
                .pipe(takeUntil(this.destroy$))
                .subscribe(
                    (response) => {
                        if (this.data.existingConnector) {
                            this.toastr.success(`${this.data.connector.tag} was modified.`);
                        } else {
                            this.toastr.success(`${this.data.connector.tag} connector configuration was added.`);
                        }
                        this.dialogRef.close(true);
                    },
                    (error) => {
                        this.loading = false;
                        this.toastr.error(error.error, `An error occured while adding or modifying this configured-connector`);
                    },
                );
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
